import { isBrowser } from 'ws-scripts/modules/environment';
import { SET_MEDIA } from './types';
import { sortVideos } from '../helpers/videos';
import {
	getStockPhotoImages,
	normalizeImages,
	deduplicateImages,
	filterVideosByLocale
} from '../helpers/imageSelection';

const GALLERY_TYPES = ['image', 'html'];

const insertWIAPIContent = (imagesToDisplay, galleryContent) => {
	const toItem = (obj) => {
		return {
			vehicle: galleryContent.vehicle,
			thumbnail: obj.thumbnail || obj.src,
			...obj
		};
	};

	const primaryContent = galleryContent.primary
		.filter((obj) => GALLERY_TYPES.includes(obj.type))
		.map(toItem);

	const secondaryContent = galleryContent.secondary
		.filter((obj) => GALLERY_TYPES.includes(obj.type))
		.map(toItem);

	const lastContent = galleryContent.last
		.filter((obj) => GALLERY_TYPES.includes(obj.type))
		.map(toItem);

	// Splice in secondary images before the others so that the order
	// will be correct even if there are no images in the incoming array.
	imagesToDisplay.splice(1, 0, ...secondaryContent);
	imagesToDisplay.push(...lastContent);
	imagesToDisplay.unshift(...primaryContent);
};

const handleOffsetSharedVehicleImageByOne = (
	imagesToDisplay,
	accountId,
	vehicleAccountId,
	offsetSharedVehicleImageByOne,
	useStockPhotosForOffsiteInventory
) => {
	// If a vehicle's accountId does not match the widget's accountId,
	// we can assume this is a shared vehicle, and thus should not
	// include the first image, because it often features
	// an overlay featuring the wrong dealership
	if (
		accountId !== vehicleAccountId &&
		offsetSharedVehicleImageByOne === 'true' &&
		// if showing only stock photos don't offset
		useStockPhotosForOffsiteInventory !== 'true'
	) {
		imagesToDisplay.shift();
	}
};

const filterFailedImages = (imagesToDisplay, failedImages) => {
	return imagesToDisplay.filter(
		(imgObj) => !failedImages.includes(imgObj.src)
	);
};

const getImagesToDisplay = (
	images,
	galleryContent,
	accountId,
	vehicleAccountId,
	offsetSharedVehicleImageByOne,
	useStockPhotosForOffsiteInventory,
	failedImages,
	nonWiapiImageLimit = null
) => {
	let imagesToDisplay;
	// remove platform images if insertMethod is set to replace
	if (galleryContent && galleryContent.insertMethod === 'replace') {
		imagesToDisplay = [];
	} else {
		imagesToDisplay = [...images];
	}
	handleOffsetSharedVehicleImageByOne(
		imagesToDisplay,
		accountId,
		vehicleAccountId,
		offsetSharedVehicleImageByOne,
		useStockPhotosForOffsiteInventory
	);
	if (nonWiapiImageLimit !== null) {
		imagesToDisplay.splice(nonWiapiImageLimit);
	}
	if (galleryContent) {
		insertWIAPIContent(imagesToDisplay, galleryContent);
	}
	imagesToDisplay = filterFailedImages(imagesToDisplay, failedImages);
	return imagesToDisplay;
};

export const setMedia = (
	media,
	locale,
	showStockPhotosForOffsiteInventory,
	showAllStockPhotosIfNoCustomPhotos,
	accountId,
	vehicleAccountId,
	offsetSharedVehicleImageByOne,
	useStockPhotosForOffsiteInventory,
	nonWiapiImageLimit,
	stockPhotoImageSourceArray,
	showAllAvailableStockPhotos,
	oemStockPhotoPreferencesEnabledFlag
) => {
	return (dispatch, getState) => {
		const {
			media: { failedImages }
		} = getState();

		const images = getStockPhotoImages(
			media,
			showStockPhotosForOffsiteInventory,
			showAllStockPhotosIfNoCustomPhotos,
			stockPhotoImageSourceArray,
			showAllAvailableStockPhotos,
			oemStockPhotoPreferencesEnabledFlag
		);

		const imagesArr = deduplicateImages(normalizeImages(images));

		const filteredVideos = filterVideosByLocale(media.videos, locale);
		const sortedVideos = sortVideos(filteredVideos);

		return dispatch({
			type: SET_MEDIA,
			payload: {
				images: imagesArr,
				videos: sortedVideos,
				imagesToDisplay: getImagesToDisplay(
					imagesArr,
					null /* galleryContent */,
					accountId,
					vehicleAccountId,
					offsetSharedVehicleImageByOne,
					useStockPhotosForOffsiteInventory,
					failedImages,
					nonWiapiImageLimit
				)
			}
		});
	};
};

const parseLimitString = (paramString) => {
	let limit = null;
	try {
		if (paramString.includes('_limitVehicleMedia=')) {
			limit = parseInt(
				paramString.match(/_limitVehicleMedia=(\d+)/)[1],
				10
			);
		}
	} catch (_e) {
		/* Purposefully do nothing */
	}
	return limit;
};

const parseLimitFromLocation = () => {
	return isBrowser ? parseLimitString(document.location.search) : null;
};

export function wiapiMediaUpdated(
	galleryContent,
	offsetSharedVehicleImageByOne,
	useStockPhotosForOffsiteInventory
) {
	return (dispatch, getState) => {
		const {
			widgetInfo: {
				vehicle: { accountId: vehicleAccountId },
				accountId
			},
			media: { images, failedImages }
		} = getState();

		const imagesToDisplay = getImagesToDisplay(
			images,
			galleryContent,
			accountId,
			vehicleAccountId,
			offsetSharedVehicleImageByOne,
			useStockPhotosForOffsiteInventory,
			failedImages,
			parseLimitFromLocation()
		);

		return dispatch({
			type: SET_MEDIA,
			payload: {
				galleryContent,
				imagesToDisplay
			}
		});
	};
}
